import { useQuery } from '@tanstack/react-query';
import { Checkbox, Form, Select } from 'antd';
import useEmployeeNoticesT from '@/components/email-templates/useEmployeeNoticesT';
import { getCompanyMailPeople } from '@/services/equipmentRequests.service';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAILS = 10;

/**
 * Who exactly receives a flow's mails, on the company's ZAZ and equipment
 * settings: people on the platform, the employee's sector head, whoever
 * started the request, typed-in addresses. Lives in the surrounding form
 * under `mailRecipients`. Nothing set = the platform's usual recipients.
 *
 * @param {string} props.companyId
 * @param {string} props.hint - What the list means for this flow.
 */
export default function MailRecipientsField({ companyId, hint }) {
  const { tn } = useEmployeeNoticesT();
  const { data, isLoading } = useQuery({
    queryKey: ['company-mail-people', companyId],
    queryFn: () => getCompanyMailPeople(companyId),
    enabled: !!companyId,
    retry: false,
  });
  const options = (data?.people || []).map((person) => ({
    value: person._id,
    label: `${person.name} (${person.email})`,
  }));

  const emailsRule = {
    validator: (_, value) => {
      const bad = (value || []).find((email) => !EMAIL_PATTERN.test(String(email).trim()));
      if (bad) return Promise.reject(new Error(tn('mailRecipients.invalidEmail', { email: bad })));
      if ((value || []).length > MAX_EMAILS) {
        return Promise.reject(new Error(tn('mailRecipients.tooMany', { max: MAX_EMAILS })));
      }
      return Promise.resolve();
    },
  };

  return (
    <div className="mt-4 grid gap-2 rounded-lg border border-zinc-200 bg-zinc-50/60 p-2.5">
      <div>
        <div className="text-[12.5px] font-bold text-zinc-700">{tn('mailRecipients.title')}</div>
        <p className="m-0 text-[12px] text-zinc-500">{hint}</p>
      </div>
      <div>
        <div className="mb-1 text-[11.5px] font-semibold text-zinc-500">{tn('mailRecipients.people')}</div>
        <Form.Item name={['mailRecipients', 'userIds']} className="!mb-0">
          <Select
            mode="multiple"
            size="small"
            allowClear
            showSearch
            loading={isLoading}
            optionFilterProp="label"
            options={options}
            placeholder={tn('mailRecipients.peoplePlaceholder')}
            labelRender={({ label }) => label || tn('mailRecipients.unknownPerson')}
          />
        </Form.Item>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <Form.Item name={['mailRecipients', 'sectorHeads']} valuePropName="checked" noStyle>
          <Checkbox>{tn('mailRecipients.sectorHeads')}</Checkbox>
        </Form.Item>
        <Form.Item name={['mailRecipients', 'requester']} valuePropName="checked" noStyle>
          <Checkbox>{tn('mailRecipients.requester')}</Checkbox>
        </Form.Item>
      </div>
      <div>
        <div className="mb-1 text-[11.5px] font-semibold text-zinc-500">{tn('mailRecipients.emails')}</div>
        <Form.Item name={['mailRecipients', 'emails']} className="!mb-0" rules={[emailsRule]}>
          <Select
            mode="tags"
            size="small"
            tokenSeparators={[',', ';', ' ']}
            open={false}
            suffixIcon={null}
            placeholder={tn('mailRecipients.emailsPlaceholder')}
          />
        </Form.Item>
      </div>
      <p className="m-0 text-[11.5px] text-zinc-500">{tn('mailRecipients.noRights')}</p>
    </div>
  );
}
